import { useEffect, useState } from "react";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import {
  Accessibility,
  ArrowLeftRight,
  Check,
  ChevronLeft,
  Copy,
  Dog,
  Lightbulb,
  PenLine,
} from "lucide-react";
import { sileo } from "sileo";
import { CardConfirmRow } from "@/components/CardConfirmRow";
import { FeatureToggleChip } from "@/components/FeatureToggleChip";
import { TableExplorer } from "@/components/TableExplorer";
import { WaitlistDryRunHint } from "@/components/WaitlistDryRunHint";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WhatsAppIcon } from "@/components/icons";
import { SectionLabel } from "@/components/modal-fields";
import { NO_SHOW_DETAIL_CLASS } from "@/components/NoShowButton";
import { formatWaitTime, tableNamesLabel } from "@/components/WaitlistCard";
import { useWaitlistSeatPicker } from "@/hooks/use-waitlist-seat-picker";
import {
  useWaitlist,
  useWaitlistNoShow,
  useWaitlistNotify,
  useWaitlistRevertNoShow,
  useWaitlistUpdate,
} from "@/hooks/use-waitlist";
import { readErrorDetail } from "@/lib/reservation-api";
import { useAppStore } from "@/store/store";
import { dayjs } from "@/utils/date";
import type { ApiWaitlistEntry } from "@/types/api";

const FEATURE_LABELS: Record<string, string> = {
  pet_friendly: "Mascota",
  accessible: "Accesibilidad",
};

const WAITLIST_ORIGIN_LABELS: Record<string, string> = {
  staff: "Staff",
  self_qr: "QR",
};

/**
 * Detail panel for a single waiting entry. Wraps the body in a thin outer
 * component so the seat-picker hook only fires when the entry is resolved
 * — keeping hook order stable while still letting the panel render nothing
 * for a stale id (e.g. mid-no-show invalidation).
 */
export function WaitlistDetailView({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const waitlist = useWaitlist();
  const entry = waitlist.data?.find((e) => e.id === entryId) ?? null;

  // Close when the entry drops out of the waiting queue so we never render a
  // stale id (the no-show action below removes it from the list).
  useEffect(() => {
    if (waitlist.data && !entry) onClose();
  }, [waitlist.data, entry, onClose]);

  if (!entry) return null;
  return <WaitlistDetailBody entry={entry} onClose={onClose} />;
}

function WaitlistDetailBody({ entry, onClose }: { entry: ApiWaitlistEntry; onClose: () => void }) {
  const tables = useAppStore((s) => s.tables);
  const zones = useAppStore((s) => s.zones);
  const noShow = useWaitlistNoShow();
  const notify = useWaitlistNotify();
  const revertNoShow = useWaitlistRevertNoShow();
  const { copiedKey, copy: copyToClipboard } = useCopyToClipboard();
  const update = useWaitlistUpdate();
  const seatPicker = useWaitlistSeatPicker(entry, { onSeated: onClose });
  const [notesDraft, setNotesDraft] = useState("");
  const [noShowConfirmOpen, setNoShowConfirmOpen] = useState(false);

  // Inline edit mode — pencil icon by the guest name toggles. Identity +
  // pax + zone + features + guest notes go editable; internal notes
  // stay separate (autosave on blur, like the reservation detail). Save
  // hits PATCH /api/waitlist/{id}/.
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(entry.guest_name);
  const [editPhone, setEditPhone] = useState(entry.guest_phone ?? "");
  const [editEmail, setEditEmail] = useState(entry.guest_email ?? "");
  const [editPax, setEditPax] = useState(entry.pax);
  const [editZone, setEditZone] = useState<string | null>(entry.preferred_zone_id ?? null);
  const [editFeatures, setEditFeatures] = useState<string[]>(entry.features);
  const [editNotes, setEditNotes] = useState(entry.notes ?? "");

  // Sync drafts when we switch entries. Keyed on the entry id so local
  // typing isn't clobbered mid-edit.
  useEffect(() => {
    setNotesDraft(entry.internal_notes ?? "");
    setIsEditing(false);
    setEditName(entry.guest_name);
    setEditPhone(entry.guest_phone ?? "");
    setEditEmail(entry.guest_email ?? "");
    setEditPax(entry.pax);
    setEditZone(entry.preferred_zone_id ?? null);
    setEditFeatures(entry.features);
    setEditNotes(entry.notes ?? "");
  }, [entry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the local edit flag into the same global slot the
  // reservation Sidebar uses, so DashboardLayout's bottom CTAs
  // ("Nueva reserva" + "Agregar a lista de espera") hide while
  // the waitlist edit is in flight, exactly like the reservation
  // edit. Reset on unmount so a closed detail doesn't leak ``true``.
  useEffect(() => {
    const { setIsEditingReservation } = useAppStore.getState();
    setIsEditingReservation(isEditing);
    return () => {
      useAppStore.getState().setIsEditingReservation(false);
    };
  }, [isEditing]);

  const enterEdit = () => {
    setEditName(entry.guest_name);
    setEditPhone(entry.guest_phone ?? "");
    setEditEmail(entry.guest_email ?? "");
    setEditPax(entry.pax);
    setEditZone(entry.preferred_zone_id ?? null);
    setEditFeatures(entry.features);
    setEditNotes(entry.notes ?? "");
    setIsEditing(true);
  };

  const cancelEdit = () => setIsEditing(false);

  const toggleEditFeature = (feature: string) => {
    setEditFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature],
    );
  };

  const saveEdit = () => {
    update.mutate(
      {
        id: entry.id,
        body: {
          guest_name: editName.trim(),
          guest_phone: editPhone.trim(),
          guest_email: editEmail.trim(),
          pax: editPax,
          preferred_zone_id: editZone,
          features: editFeatures,
          notes: editNotes,
        },
      },
      {
        onSuccess: () => {
          sileo.success({ title: "Cambios guardados" });
          setIsEditing(false);
        },
        onError: (err) => {
          sileo.error({
            title: "No se pudo guardar",
            description: readErrorDetail(err) ?? "Intenta de nuevo en un momento.",
          });
        },
      },
    );
  };

  const phoneDigits = entry.guest_phone?.replace(/\D/g, "") ?? "";
  const zoneName = entry.preferred_zone_id
    ? (zones.find((z) => z.id === entry.preferred_zone_id)?.name ?? null)
    : null;
  const hasPet = entry.features.includes("pet_friendly");
  const hasAccessible = entry.features.includes("accessible");
  const extraFeatures = entry.features.filter((f) => f !== "pet_friendly" && f !== "accessible");

  const openWhatsApp = () => {
    if (phoneDigits) window.open(`https://wa.me/${phoneDigits}`, "_blank", "noopener");
  };

  // Internal notes autosave on blur, mirroring the reservation detail. On
  // failure we revert the draft and surface a local error.
  const handleNotesBlur = () => {
    const next = notesDraft.trim();
    const current = (entry.internal_notes ?? "").trim();
    if (next === current) return;
    update.mutate(
      { id: entry.id, body: { internal_notes: next } },
      {
        onError: (err) => {
          sileo.error({
            title: "Notas no guardadas",
            description: readErrorDetail(err) ?? "Intenta de nuevo.",
          });
          setNotesDraft(current);
        },
      },
    );
  };

  const confirmNoShow = () => {
    const targetId = entry.id;
    const guestName = entry.guest_name;
    noShow.mutate(targetId, {
      onSuccess: () => {
        onClose();
        sileo.success({
          title: "Marcado como no-show",
          description: `${guestName} salió de la cola.`,
          button: {
            title: "Revertir",
            onClick: () =>
              revertNoShow.mutate(targetId, {
                onSuccess: () =>
                  sileo.success({
                    title: "Devuelto a la cola",
                    description: `${guestName} sigue esperando.`,
                  }),
                onError: () =>
                  sileo.error({
                    title: "No se pudo revertir",
                    description: "Intenta de nuevo en un momento.",
                  }),
              }),
          },
        });
      },
      onError: () =>
        sileo.error({
          title: "No se pudo marcar",
          description: "Intenta de nuevo en un momento.",
        }),
    });
  };

  const { effectiveTableIds, isSeating } = seatPicker;

  // Dirty count for the amber save bar — same UX the reservation edit
  // uses. ``trim`` so trailing whitespace doesn't count as a change.
  const dirtyCount = isEditing
    ? [
        editName.trim() !== entry.guest_name,
        (editPhone.trim() || "") !== (entry.guest_phone || ""),
        (editEmail.trim() || "") !== (entry.guest_email || ""),
        editPax !== entry.pax,
        (editZone ?? null) !== (entry.preferred_zone_id ?? null),
        [...editFeatures].toSorted().join(",") !== [...entry.features].toSorted().join(","),
        (editNotes ?? "") !== (entry.notes ?? ""),
      ].filter(Boolean).length
    : 0;
  const canSave = isEditing && dirtyCount > 0 && editName.trim().length > 0 && !update.isPending;

  return (
    <div className="flex h-full flex-col">
      {/* Header — Volver + Editar / Editando pill, mirroring the
          reservation DetailView. Clicking Volver while editing cancels. */}
      <div className="flex items-center gap-2 border-b border-border px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={isEditing ? cancelEdit : onClose}
          className="flex items-center gap-1 font-semibold text-text-primary"
        >
          <ChevronLeft className="size-4" />
          Volver
        </Button>
        {isEditing ? (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-text-primary bg-white px-2.5 py-0.5 text-[11.5px] font-semibold text-text-primary">
            <PenLine className="size-3" />
            Editando
          </span>
        ) : (
          <button
            type="button"
            onClick={enterEdit}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-blue-400 px-2.5 py-0.5 text-[11.5px] font-semibold text-blue-700 hover:bg-blue-50"
          >
            <PenLine className="size-3" />
            Editar
          </button>
        )}
      </div>

      {/* Amber save bar — same as the reservation edit. Visible only when
          we have unsaved changes. */}
      {isEditing && dirtyCount > 0 && (
        <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-5 py-3">
          <span className="text-[14px] font-semibold text-amber-800">
            <strong className="font-bold">{dirtyCount}</strong>{" "}
            {dirtyCount === 1 ? "cambio" : "cambios"} sin guardar
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveEdit}
              disabled={!canSave}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {update.isPending ? "Guardando…" : "Guardar"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-800 hover:bg-amber-50"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {/* Content — mirrors the reservation DetailView body. */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        {/* Cliente — name + feature icons + framed contact box. */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2 text-[18px] font-extrabold leading-tight tracking-tight">
            {isEditing ? (
              <label className="group relative inline-flex min-w-[60%] items-center">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nombre"
                  className="w-full rounded-md border border-dashed border-text-secondary/60 bg-white px-2 py-0.5 text-[18px] font-extrabold leading-tight tracking-tight text-text-primary outline-none focus:border-solid focus:border-text-primary"
                  aria-label="Editar nombre"
                />
                <PenLine className="pointer-events-none absolute right-2 size-3.5 text-text-secondary/70" />
              </label>
            ) : (
              <span>{entry.guest_name}</span>
            )}
            <span className="inline-flex items-center gap-1.5">
              {isEditing ? (
                <>
                  <FeatureToggleChip
                    selected={editFeatures.includes("pet_friendly")}
                    onClick={() => toggleEditFeature("pet_friendly")}
                    label="Mascota"
                    icon={<Dog className="size-3" />}
                  />
                  <FeatureToggleChip
                    selected={editFeatures.includes("accessible")}
                    onClick={() => toggleEditFeature("accessible")}
                    label="Accesibilidad"
                    icon={<Accessibility className="size-3" />}
                  />
                </>
              ) : (
                <>
                  {hasPet && (
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#c8c8c2] bg-[#f7f6f3] text-text-secondary"
                      title="Mascota"
                    >
                      <Dog className="size-3" />
                    </span>
                  )}
                  {hasAccessible && (
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#c8c8c2] bg-[#f7f6f3] text-text-secondary"
                      title="Accesibilidad"
                    >
                      <Accessibility className="size-3" />
                    </span>
                  )}
                </>
              )}
            </span>
          </div>

          {isEditing ? (
            <div className="overflow-hidden rounded-lg border border-[#c8c8c2] bg-white">
              <div className="grid grid-cols-[22px_1fr_auto_auto] items-center gap-1.5 border-b border-[#ececea] px-2.5 py-1.5 text-[13px]">
                <span className="text-center font-mono text-[12px] text-text-secondary">☎</span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value.replace(/[^\d+\s\-()]/g, ""))}
                  placeholder="+57 300 000 0000"
                  className="rounded border border-dashed border-text-secondary/60 bg-white px-1.5 py-0.5 font-mono text-[12px] text-text-primary outline-none focus:border-solid focus:border-text-primary"
                  aria-label="Editar teléfono"
                />
                <PenLine className="size-3 text-text-secondary/70" />
                <span />
              </div>
              <div className="grid grid-cols-[22px_1fr_auto_auto] items-center gap-1.5 px-2.5 py-1.5 text-[13px]">
                <span className="text-center font-mono text-[12px] text-text-secondary">✉</span>
                <input
                  type="email"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="nombre@correo.com"
                  className="rounded border border-dashed border-text-secondary/60 bg-white px-1.5 py-0.5 text-[12.5px] text-text-primary outline-none focus:border-solid focus:border-text-primary"
                  aria-label="Editar email"
                />
                <PenLine className="size-3 text-text-secondary/70" />
                <span />
              </div>
            </div>
          ) : entry.guest_phone || entry.guest_email ? (
            <div className="overflow-hidden rounded-lg border border-[#c8c8c2] bg-white">
              {entry.guest_phone && (
                <div className="grid grid-cols-[22px_1fr_auto_auto] items-center gap-1.5 border-b border-[#ececea] px-2.5 py-1.5 text-[13px] last:border-b-0">
                  <span className="text-center font-mono text-[12px] text-text-secondary">☎</span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[12px]">
                    {entry.guest_phone}
                  </span>
                  <button
                    type="button"
                    onClick={openWhatsApp}
                    disabled={!phoneDigits}
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-[#e8f7ec] disabled:opacity-40"
                    aria-label="Enviar por WhatsApp"
                  >
                    <WhatsAppIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(entry.guest_phone, "phone")}
                    className="flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-gray-100 hover:text-text-primary"
                    aria-label={copiedKey === "phone" ? "Teléfono copiado" : "Copiar teléfono"}
                  >
                    {copiedKey === "phone" ? (
                      <Check className="size-3.5 text-green-600" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                </div>
              )}
              {entry.guest_email && (
                <div className="grid grid-cols-[22px_1fr_auto_auto] items-center gap-1.5 px-2.5 py-1.5 text-[13px]">
                  <span className="text-center font-mono text-[12px] text-text-secondary">✉</span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px]">
                    {entry.guest_email}
                  </span>
                  <span />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(entry.guest_email, "email")}
                    className="flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-gray-100 hover:text-text-primary"
                    aria-label={copiedKey === "email" ? "Email copiado" : "Copiar email"}
                  >
                    {copiedKey === "email" ? (
                      <Check className="size-3.5 text-green-600" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* En espera — same framed section card the reservation uses for RESERVA. */}
        <div className="flex flex-col gap-1.5">
          <SectionLabel>En espera</SectionLabel>
          <div className="overflow-hidden rounded-[10px] border border-[#c8c8c2] bg-[#f7f6f3]">
            <div className="p-3.5">
              <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-text-secondary">
                {dayjs(entry.created_at).format("dddd · D MMM YYYY · h:mm A")}
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div
                  className={`rounded-lg border bg-white px-3 py-2 ${
                    isEditing ? "border-dashed border-text-secondary/60" : "border-[#c8c8c2]"
                  }`}
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Tiempo espera
                  </div>
                  <div className="font-mono text-[22px] font-extrabold leading-none text-text-primary">
                    {formatWaitTime(entry.created_at).replace(/^hace\s+/, "")}
                  </div>
                </div>
                <div
                  className={`rounded-lg border bg-white px-3 py-2 ${
                    isEditing ? "border-dashed border-text-secondary/60" : "border-[#c8c8c2]"
                  }`}
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Personas
                    {isEditing && <PenLine className="size-2.5" />}
                  </div>
                  {isEditing ? (
                    <div className="flex items-center justify-between gap-1">
                      <button
                        type="button"
                        onClick={() => setEditPax((p) => Math.max(1, p - 1))}
                        className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3]"
                        aria-label="Quitar una persona"
                      >
                        −
                      </button>
                      <span className="font-mono text-[22px] font-extrabold leading-none text-text-primary">
                        {editPax}
                        <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                          pax
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditPax((p) => Math.min(20, p + 1))}
                        className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3]"
                        aria-label="Agregar una persona"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <div className="font-mono text-[22px] font-extrabold leading-none">
                      {entry.pax}
                      <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                        pax
                      </span>
                    </div>
                  )}
                </div>
                <div
                  className={`col-span-2 flex flex-col gap-0.5 rounded-lg border bg-white px-3 py-2 ${
                    isEditing || !zoneName
                      ? "border-dashed border-text-secondary/60"
                      : "border-[#c8c8c2]"
                  }`}
                >
                  <span className="flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Zona
                    {isEditing && <PenLine className="size-2.5" />}
                  </span>
                  {isEditing ? (
                    <select
                      value={editZone ?? ""}
                      onChange={(e) => setEditZone(e.target.value || null)}
                      className="w-full bg-transparent text-[14px] font-bold leading-tight outline-none"
                      aria-label="Editar zona preferida"
                    >
                      <option value="">Sin zona</option>
                      {zones
                        .filter((z) => !z.isBlock)
                        .map((z) => (
                          <option key={z.id} value={z.id}>
                            {z.name}
                          </option>
                        ))}
                    </select>
                  ) : zoneName ? (
                    <span className="text-[14px] font-bold leading-tight">{zoneName}</span>
                  ) : (
                    <span className="text-[12px] leading-tight text-text-secondary">Sin zona</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notas del cliente — edit mode renders a plain textarea
            (mirrors the reservation edit form). Read mode keeps the
            cream quote box so the comensal's voice reads distinct. */}
        {isEditing ? (
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-text-secondary">Notas del cliente</Label>
            <Textarea
              rows={2}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Lo que pidió el comensal…"
              className="resize-none text-xs"
            />
          </div>
        ) : (
          (entry.notes?.trim() || extraFeatures.length > 0) && (
            <div className="flex flex-col gap-1.5">
              <div className="text-[13px] font-bold text-text-primary">Notas del cliente</div>
              <div className="rounded-lg border border-[#e8e2cc] bg-[#fdfbf2] px-3 py-2.5 leading-snug">
                {entry.notes?.trim() ? (
                  <p className="text-[14px] italic leading-relaxed text-text-primary">
                    {entry.notes}
                  </p>
                ) : (
                  <span className="text-[13px] text-text-secondary">Sin notas del cliente</span>
                )}
                {extraFeatures.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {extraFeatures.map((f) => (
                      <span
                        key={f}
                        className="rounded-full border border-[#e8e2cc] bg-white px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                      >
                        {FEATURE_LABELS[f] ?? f}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-text-secondary">
                  Origen · {WAITLIST_ORIGIN_LABELS[entry.origin] ?? entry.origin}
                </div>
              </div>
            </div>
          )
        )}

        {/* Mesa sugerida — framed tile (same light-gray border used by the
            other detail blocks) with the card's chip + ↔ pattern inside.
            Tap the chip → opens the TableExplorer drawer. Hidden in edit
            mode: identity/pax/features are the only things the amber save
            bar commits; seating is a separate action. */}
        {!isEditing && (
          <div className="flex flex-col gap-1 rounded-[10px] border border-[#c8c8c2] bg-[#f7f6f3] px-3 py-2">
            <span className="inline-flex items-center gap-1 font-mono text-[10px] font-medium uppercase tracking-widest text-[#8a6d00]">
              <Lightbulb className="size-3" />
              Mesa sugerida
            </span>
            {effectiveTableIds ? (
              <button
                type="button"
                onClick={seatPicker.openExplorer}
                className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded border border-dashed border-[#b88a00] bg-[#fffbeb] px-2 py-0.5 font-mono text-[15px] font-bold leading-none text-[#8a6d00] transition hover:border-[#8a6d00] hover:bg-[#fff5d4]"
                aria-label={`Cambiar ${tableNamesLabel(tables, effectiveTableIds)}`}
              >
                {tableNamesLabel(tables, effectiveTableIds)}
                <ArrowLeftRight className="size-3 opacity-70" />
              </button>
            ) : (
              // No auto-fit suggestion: still expose the Explorer so the
              // host can pick (or redistribute) a table manually. Same
              // amber chip the card uses for visual continuity.
              <button
                type="button"
                onClick={seatPicker.openExplorer}
                className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded border border-dashed border-[#b88a00] bg-[#fffbeb] px-2 py-0.5 font-mono text-[15px] font-bold leading-none text-[#8a6d00] transition hover:border-[#8a6d00] hover:bg-[#fff5d4]"
                aria-label="Elegir mesa manualmente"
              >
                Sin mesa libre
                <ArrowLeftRight className="size-3 opacity-70" />
              </button>
            )}
            {effectiveTableIds && (
              <WaitlistDryRunHint
                entry={entry}
                tableIds={effectiveTableIds}
                startsAt={seatPicker.seatWindow.startsAt}
                endsAt={seatPicker.seatWindow.endsAt}
              />
            )}
          </div>
        )}

        {/* Notas internas — editable, autosave on blur (same as the reservation
            detail). Backend: PATCH /api/waitlist/{id}/ accepts internal_notes. */}
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Notas internas</SectionLabel>
          <Textarea
            rows={2}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={handleNotesBlur}
            placeholder="Sin notas del host…"
            className="resize-none rounded-lg border-dashed bg-white text-[13px] text-text-primary"
          />
        </div>

        {/* Acciones — split into two steps to match the host's real
            flow. Waiting entries get "Avisar" (tells the guest the
            table is ready, guest's page swaps to "tu mesa está
            lista"); notified entries get "Sentar a la mesa" once the
            guest physically arrives. No-show stays available in both
            and swaps its button for an inline CardConfirmRow
            (destructive). Hidden during edit mode: the amber save bar
            is the only commit affordance. */}
        {!isEditing && !noShowConfirmOpen && (
          <div className="flex flex-col gap-2">
            {entry.status === "notified" ? (
              <Button
                variant="outline"
                disabled={!effectiveTableIds || isSeating}
                onClick={() => {
                  if (effectiveTableIds) seatPicker.seatNow(effectiveTableIds);
                }}
                className="w-full justify-center text-[13px] font-semibold"
              >
                {effectiveTableIds ? "▶ Sentar a la mesa" : "Sin mesa libre"}
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={!effectiveTableIds || notify.isPending}
                onClick={() => notify.mutate(entry.id)}
                className="w-full justify-center border-emerald-600 bg-emerald-600 text-[13px] font-semibold text-white hover:bg-emerald-700 hover:text-white"
              >
                {effectiveTableIds ? "▶ Avisar al comensal" : "Sin mesa libre"}
              </Button>
            )}
            <Button
              variant="outline"
              disabled={noShow.isPending}
              onClick={() => setNoShowConfirmOpen(true)}
              className={`w-full justify-center text-[13px] font-semibold ${NO_SHOW_DETAIL_CLASS}`}
            >
              No-show
            </Button>
          </div>
        )}

        {!isEditing && noShowConfirmOpen && (
          <CardConfirmRow
            tone="warn"
            label="¿Confirmar No-Show?"
            yesLabel="Sí, no-show"
            onYes={() => {
              confirmNoShow();
              setNoShowConfirmOpen(false);
            }}
            onNo={() => setNoShowConfirmOpen(false)}
          />
        )}
      </div>

      <TableExplorer {...seatPicker.explorerProps} />
    </div>
  );
}
